import { getFirebaseAuth } from '../firebase/config';
import api from './api';

// Helper function to detect mobile devices
function isMobileDevice() {
  const userAgent = navigator.userAgent || navigator.vendor || window.opera;
  
  // Check for mobile patterns in user agent
  const mobilePatterns = /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini|mobile|tablet/i;
  
  // Also check for touch support and small screen size
  const isTouchDevice = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
  const isSmallScreen = window.innerWidth <= 768;
  
  return mobilePatterns.test(userAgent) || (isTouchDevice && isSmallScreen);
}

// Helper function to send token to backend
async function sendTokenToBackend(provider, idToken, firebaseUser) {
  try {
    console.log(`Sending ${provider} token to backend:`, idToken.substring(0, 20) + '...');
    console.log('Full token length:', idToken.length);
    
    // Log user data being sent
    console.log('Firebase user data:', {
      displayName: firebaseUser.displayName,
      email: firebaseUser.email,
      uid: firebaseUser.uid,
      photoURL: firebaseUser.photoURL,
      providerId: firebaseUser.providerId
    });
    
    // Prepare user data
    const userData = {
      name: firebaseUser.displayName,
      email: firebaseUser.email,
      photo: firebaseUser.photoURL
    };
    console.log('Formatted user data for backend:', userData);
    
    // For debugging, log the API configuration
    console.log('API URL:', api.defaults.baseURL || 'Not set - using relative URL');
    console.log('API endpoint:', `/auth/${provider}`);
    
    // Log the full request URL using the configured API URL from api.js
    console.log('Full request URL:', `${api.defaults.baseURL}/auth/${provider}`);
    
    // Log the request payload (without showing the full token)
    console.log('Request payload:', { 
      token: idToken.substring(0, 20) + '...' + idToken.substring(idToken.length - 5), 
      user: userData 
    });
    
    // Make the API request with a timeout
    const response = await api.post(`/auth/${provider}`, { 
      token: idToken,
      user: userData
    }, {
      timeout: 15000, // 15 second timeout
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      }
    });
    
    console.log(`${provider} authentication response status:`, response.status);
    console.log(`${provider} authentication response data:`, response.data);
    
    if (response.data.token) {
      console.log('Storing authentication token in localStorage');
      localStorage.setItem('token', response.data.token);
      localStorage.setItem('user', JSON.stringify(response.data.user));
      
      // Set a flag in localStorage to indicate this is a recent sign-up
      // This helps the API interceptor identify new sign-ups
      localStorage.setItem('recentSignUp', 'true');
      console.log('Set recentSignUp flag in localStorage');
      
      // Verify the data was stored correctly
      const storedToken = localStorage.getItem('token');
      const storedUser = localStorage.getItem('user');
      console.log('Token stored successfully:', !!storedToken);
      console.log('User stored successfully:', !!storedUser);
    } else {
      console.warn('No token received in response');
      throw new Error('Authentication failed: No token received from server');
    }
    
    return response.data;
  } catch (error) {
    console.error(`${provider} token verification error:`, error);
    
    // Create a detailed error log
    let errorDetails = `Error during ${provider} authentication:\n`;
    
    // Log detailed error information
    if (error.response) {
      // The request was made and the server responded with a status code
      // that falls out of the range of 2xx
      console.error('Response status:', error.response.status);
      console.error('Response headers:', error.response.headers);
      console.error('Response data:', error.response.data);
      
      errorDetails += `Status: ${error.response.status}\n`;
      errorDetails += `Server message: ${JSON.stringify(error.response.data)}\n`;
      
      // Create a more user-friendly error message based on the status code
      let errorMessage = '';
      if (error.response.status === 401) {
        errorMessage = 'Authentication failed. The token was invalid or expired.';
      } else if (error.response.status === 500) {
        errorMessage = 'Server error. Please try again later.';
      } else {
        errorMessage = error.response.data.message || 'An error occurred during authentication.';
      }
      
      const customError = new Error(errorMessage);
      customError.details = errorDetails;
      customError.originalError = error;
      customError.responseData = error.response.data;
      throw customError;
    } else if (error.request) {
      // The request was made but no response was received
      console.error('Request was made but no response received:', error.request);
      errorDetails += 'No response received from server. Please check your internet connection.\n';
      
      const customError = new Error('Network error. Please check your internet connection and try again.');
      customError.details = errorDetails;
      customError.originalError = error;
      throw customError;
    } else {
      // Something happened in setting up the request that triggered an Error
      console.error('Error message:', error.message);
      errorDetails += `Error: ${error.message}\n`;
      
      // Just rethrow the original error if it's already a custom error from our code
      throw error;
    }
  }
}

const FirebaseAuthService = {
  // Google Sign-In
  googleSignIn: async () => {
    try {
      console.log('Starting Google sign-in process...');
      
      // Detect if user is on mobile device
      const isMobile = isMobileDevice();
      console.log('Device type:', isMobile ? 'Mobile' : 'Desktop');
      
      // OPTIMIZATION: Lazy load Firebase and auth methods
      const auth = await getFirebaseAuth();
      const { GoogleAuthProvider, signInWithPopup, signInWithRedirect, setPersistence, browserLocalPersistence } = await import('firebase/auth');
      
      const provider = new GoogleAuthProvider();
      
      // Add scopes for email and profile
      provider.addScope('email');
      provider.addScope('profile');
      console.log('Added scopes for Google auth');
      
      // Set a flag to indicate we're in the middle of authentication
      sessionStorage.setItem('authInProgress', 'true');
      localStorage.setItem('authRedirectInProgress', 'true');
      
      // Use redirect flow for mobile devices, popup for desktop
      if (isMobile) {
        console.log('Using redirect flow for mobile device authentication');
        
        // CRITICAL: Set persistence to LOCAL before redirect
        // This ensures Firebase persists auth state after redirect
        console.log('Setting Firebase persistence to browserLocalPersistence...');
        await setPersistence(auth, browserLocalPersistence);
        console.log('✓ Persistence set to LOCAL');
        
        // Redirect flow - user will be redirected to Firebase domain and back
        await signInWithRedirect(auth, provider);
        // Note: Code execution stops here as browser redirects
        // The redirect result will be handled by handleRedirectResult on return
        return null;
      } else {
        console.log('Using popup flow for desktop authentication');
        const result = await signInWithPopup(auth, provider);
        console.log('Popup auth completed successfully');
        
        // Clear the auth in progress flag since we succeeded with popup
        sessionStorage.removeItem('authInProgress');
        
        // Get the ID token
        console.log('Getting ID token from Firebase user');
        const idToken = await result.user.getIdToken();
        console.log('ID token obtained, length:', idToken.length);
        console.log('Token preview:', idToken.substring(0, 20) + '...');
        
        // Send token to backend
        console.log('Sending token to backend...');
        return await sendTokenToBackend('google', idToken, result.user);
      }
    } catch (error) {
      console.error('Google sign-in error:', error);
      // Clear the auth flag on error
      sessionStorage.removeItem('authInProgress');
      
      // Log detailed error information
      if (error.code) {
        console.error('Error code:', error.code);
      }
      if (error.message) {
        console.error('Error message:', error.message);
      }
      if (error.email) {
        console.error('Error email:', error.email);
      }
      if (error.credential) {
        console.error('Error credential:', error.credential);
      }
      
      // Create a more user-friendly error message
      let errorMessage = 'Authentication failed. ';
      
      // Handle specific error codes
      if (error.code === 'auth/popup-closed-by-user') {
        errorMessage += 'The sign-in popup was closed before authentication was completed.';
      } else if (error.code === 'auth/popup-blocked') {
        errorMessage += 'The sign-in popup was blocked by your browser. Please allow popups for this site.';
      } else if (error.code === 'auth/cancelled-popup-request') {
        errorMessage += 'The authentication request was cancelled.';
      } else if (error.code === 'auth/network-request-failed') {
        errorMessage += 'A network error occurred. Please check your internet connection and try again.';
      } else {
        errorMessage += error.message || 'Please try again or use another sign-in method.';
      }
      
      // Create a custom error object with the user-friendly message
      const customError = new Error(errorMessage);
      customError.originalError = error;
      throw customError;
    }
  },

  // Handle redirect result (for mobile devices) using onAuthStateChanged
  handleRedirectResult: async () => {
    return new Promise(async (resolve, reject) => {
      try {
        console.log('🔍 Setting up auth state listener for redirect...');
        
        // OPTIMIZATION: Lazy load Firebase and auth methods
        const auth = await getFirebaseAuth();
        const { onAuthStateChanged, getRedirectResult } = await import('firebase/auth');
        
        // First try getRedirectResult (works on desktop/some mobile browsers)
        const redirectResult = await getRedirectResult(auth);
        
        if (redirectResult && redirectResult.user) {
          console.log('✓ getRedirectResult found user:', redirectResult.user.email);
          
          const provider = redirectResult.credential?.providerId?.includes('apple') ? 'apple' : 
                          redirectResult.user.providerData?.[0]?.providerId?.includes('apple') ? 'apple' : 'google';
          
          const idToken = await redirectResult.user.getIdToken();
          const result = await sendTokenToBackend(provider, idToken, redirectResult.user);
          resolve(result);
          return;
        }
        
        console.log('getRedirectResult returned null, using onAuthStateChanged...');
        
        // If getRedirectResult fails, use onAuthStateChanged (more reliable on mobile)
        // This fires when user is authenticated after redirect
        let hasResolved = false;
        const timeoutId = setTimeout(() => {
          if (!hasResolved) {
            console.log('⏱️ Timeout: No auth state change detected');
            hasResolved = true;
            resolve(null);
          }
        }, 3000); // Wait max 3 seconds for auth state
        
        const unsubscribe = onAuthStateChanged(auth, async (user) => {
          if (hasResolved) return;
          
          if (user) {
            console.log('✓ onAuthStateChanged detected user:', user.email);
            hasResolved = true;
            clearTimeout(timeoutId);
            unsubscribe();
            
            // Determine provider from user data
            const provider = user.providerData?.[0]?.providerId?.includes('apple') ? 'apple' : 'google';
            console.log('✓ Provider:', provider);
            
            try {
              const idToken = await user.getIdToken();
              console.log('✓ ID token obtained');
              
              const result = await sendTokenToBackend(provider, idToken, user);
              resolve(result);
            } catch (error) {
              console.error('Error processing auth state:', error);
              reject(error);
            }
          } else {
            console.log('onAuthStateChanged: No user (normal page load)');
            clearTimeout(timeoutId);
            unsubscribe();
            hasResolved = true;
            resolve(null);
          }
        });
      } catch (error) {
        console.error('❌ Redirect result error:', error);
        reject(error);
      }
    });
  },

  // Apple Sign-In
  appleSignIn: async () => {
    try {
      console.log('Starting Apple sign-in process...');
      
      // Detect if user is on mobile device
      const isMobile = isMobileDevice();
      console.log('Device type:', isMobile ? 'Mobile' : 'Desktop');
      
      // OPTIMIZATION: Lazy load Firebase and auth methods
      const auth = await getFirebaseAuth();
      const { OAuthProvider, signInWithPopup, signInWithRedirect, setPersistence, browserLocalPersistence } = await import('firebase/auth');
      
      const provider = new OAuthProvider('apple.com');
      
      // Add scopes for email and name
      provider.addScope('email');
      provider.addScope('name');
      console.log('Added scopes for Apple auth');
      
      // Set a flag to indicate we're in the middle of authentication
      sessionStorage.setItem('authInProgress', 'true');
      localStorage.setItem('authRedirectInProgress', 'true');
      
      // Use redirect flow for mobile devices, popup for desktop
      if (isMobile) {
        console.log('Using redirect flow for mobile device Apple authentication');
        
        // CRITICAL: Set persistence to LOCAL before redirect
        // This ensures Firebase persists auth state after redirect
        console.log('Setting Firebase persistence to browserLocalPersistence...');
        await setPersistence(auth, browserLocalPersistence);
        console.log('✓ Persistence set to LOCAL');
        
        // Redirect flow - user will be redirected to Firebase domain and back
        await signInWithRedirect(auth, provider);
        // Note: Code execution stops here as browser redirects
        // The redirect result will be handled by handleRedirectResult on return
        return null;
      } else {
        console.log('Using popup flow for desktop Apple authentication');
        const result = await signInWithPopup(auth, provider);
        console.log('Apple popup auth completed successfully');
        
        // Clear the auth in progress flag since we succeeded with popup
        sessionStorage.removeItem('authInProgress');
        
        // Get the ID token
        console.log('Getting ID token from Firebase user');
        const idToken = await result.user.getIdToken();
        console.log('Apple ID token obtained, length:', idToken.length);
        console.log('Apple token preview:', idToken.substring(0, 20) + '...');
        
        // Send token to backend
        console.log('Sending Apple token to backend...');
        return await sendTokenToBackend('apple', idToken, result.user);
      }
    } catch (error) {
      console.error('Apple sign-in error:', error);
      // Clear the auth flag on error
      sessionStorage.removeItem('authInProgress');
      
      // Log detailed error information
      if (error.code) {
        console.error('Error code:', error.code);
      }
      if (error.message) {
        console.error('Error message:', error.message);
      }
      if (error.email) {
        console.error('Error email:', error.email);
      }
      if (error.credential) {
        console.error('Error credential:', error.credential);
      }
      
      // Create a more user-friendly error message
      let errorMessage = 'Apple authentication failed. ';
      
      // Handle specific error codes
      if (error.code === 'auth/popup-closed-by-user') {
        errorMessage += 'The sign-in popup was closed before authentication was completed.';
      } else if (error.code === 'auth/popup-blocked') {
        errorMessage += 'The sign-in popup was blocked by your browser. Please allow popups for this site.';
      } else if (error.code === 'auth/cancelled-popup-request') {
        errorMessage += 'The authentication request was cancelled.';
      } else if (error.code === 'auth/network-request-failed') {
        errorMessage += 'A network error occurred. Please check your internet connection and try again.';
      } else {
        errorMessage += error.message || 'Please try again or use another sign-in method.';
      }
      
      // Create a custom error object with the user-friendly message
      const customError = new Error(errorMessage);
      customError.originalError = error;
      throw customError;
    }
  },

  // Sign out
  signOut: async () => {
    try {
      // OPTIMIZATION: Lazy load Firebase and auth methods
      const auth = await getFirebaseAuth();
      const { signOut } = await import('firebase/auth');
      
      await signOut(auth);
      // Clear local storage
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      return { success: true };
    } catch (error) {
      console.error('Sign out error:', error);
      throw error;
    }
  }
};

export default FirebaseAuthService;
